// 1D LUT (256 段) を生成する純ロジック。levels / curves / brightness-contrast / exposure /
// invert / posterize / threshold はこれで表現する。GLSL 側は LUT テクスチャを引くだけ。
// gradient-map も 256 段の RGB LUT (輝度 → 色)。

import type { Adjustment, CurvePoints, LevelsChannel, SelectiveColorRange } from '@/types/document'
import { SELECTIVE_COLOR_RANGES } from '@/types/document'
import { pixelStore } from '@/pixels/pixelStore'
import { sampleTiledLut } from './cube'
import { hexToRgb } from '@/color/convert'

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)

export function levelsLut(l: LevelsChannel): Uint8ClampedArray {
  const out = new Uint8ClampedArray(256)
  const inB = l.inBlack, inW = Math.max(l.inWhite, l.inBlack + 1)
  for (let i = 0; i < 256; i++) {
    let v = clamp01((i - inB) / (inW - inB))
    v = Math.pow(v, 1 / l.gamma)
    out[i] = Math.round(l.outBlack + v * (l.outWhite - l.outBlack))
  }
  return out
}

/** 制御点を通る単調な区分 3 次補間 (Fritsch–Carlson)。Photoshop の曲線に近い滑らかさ */
export function curveLut(points: CurvePoints): Uint8ClampedArray {
  const pts = [...points].sort((a, b) => a[0] - b[0])
  const n = pts.length
  const out = new Uint8ClampedArray(256)
  if (n === 1) return out.fill(Math.round(pts[0]![1]))
  const xs = pts.map(p => p[0]), ys = pts.map(p => p[1])
  const d: number[] = []
  for (let i = 0; i < n - 1; i++) d.push((ys[i + 1]! - ys[i]!) / Math.max(1e-6, xs[i + 1]! - xs[i]!))
  const m: number[] = new Array(n).fill(0)
  m[0] = d[0]!
  m[n - 1] = d[n - 2]!
  for (let i = 1; i < n - 1; i++) m[i] = d[i - 1]! * d[i]! <= 0 ? 0 : (d[i - 1]! + d[i]!) / 2
  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue }
    const a = m[i]! / d[i]!, b = m[i + 1]! / d[i]!
    const s = a * a + b * b
    if (s > 9) {
      const t = 3 / Math.sqrt(s)
      m[i] = t * a * d[i]!
      m[i + 1] = t * b * d[i]!
    }
  }
  for (let x = 0; x < 256; x++) {
    if (x <= xs[0]!) { out[x] = Math.round(ys[0]!); continue }
    if (x >= xs[n - 1]!) { out[x] = Math.round(ys[n - 1]!); continue }
    let i = 0
    while (i < n - 2 && x > xs[i + 1]!) i++
    const h = xs[i + 1]! - xs[i]!
    const t = (x - xs[i]!) / h
    const t2 = t * t, t3 = t2 * t
    const y = (2 * t3 - 3 * t2 + 1) * ys[i]! + (t3 - 2 * t2 + t) * h * m[i]! + (-2 * t3 + 3 * t2) * ys[i + 1]! + (t3 - t2) * h * m[i + 1]!
    out[x] = Math.round(Math.max(0, Math.min(255, y)))
  }
  return out
}

/** 明るさ・コントラスト (非レガシー)。brightness -150..150、contrast -100..100 */
export function brightnessContrastLut(brightness: number, contrast: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(256)
  const b = brightness / 150
  const c = contrast / 100
  for (let i = 0; i < 256; i++) {
    let v = i / 255
    // 明るさ: 中間調を持ち上げる (ガンマ的)
    if (b !== 0) v = b > 0 ? Math.pow(v, 1 / (1 + b * 1.5)) : Math.pow(v, 1 + -b * 1.5)
    // コントラスト: 0.5 を中心に S 字 / 平坦化
    if (c > 0) {
      const k = 1 + c * 2
      v = 0.5 + (v - 0.5) * k
      v = clamp01(v)
      // 端の飽和を和らげる
      v = v <= 0 || v >= 1 ? v : 0.5 + Math.sign(v - 0.5) * Math.pow(Math.abs(v - 0.5) * 2, 1 / (1 + c * 0.5)) / 2
    } else if (c < 0) {
      v = 0.5 + (v - 0.5) * (1 + c)
    }
    out[i] = Math.round(clamp01(v) * 255)
  }
  return out
}

/** 露光量 (リニア空間で計算)。exposure: EV、offset: -0.5..0.5、gamma: 逆ガンマ */
export function exposureLut(exposure: number, offset: number, gamma: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(256)
  const toLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4))
  const toSrgb = (v: number) => (v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055)
  for (let i = 0; i < 256; i++) {
    let v = toLinear(i / 255)
    v = v * Math.pow(2, exposure) + offset
    v = clamp01(v)
    v = Math.pow(v, 1 / gamma)
    out[i] = Math.round(clamp01(toSrgb(v)) * 255)
  }
  return out
}

export function invertLut(): Uint8ClampedArray {
  const out = new Uint8ClampedArray(256)
  for (let i = 0; i < 256; i++) out[i] = 255 - i
  return out
}

export function posterizeLut(levels: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(256)
  const n = Math.max(2, Math.round(levels))
  for (let i = 0; i < 256; i++) {
    const step = Math.min(n - 1, Math.floor((i / 256) * n))
    out[i] = Math.round((step / (n - 1)) * 255)
  }
  return out
}

export function thresholdLut(level: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(256)
  for (let i = 0; i < 256; i++) out[i] = i >= level ? 255 : 0
  return out
}

export function identityLut(): Uint8ClampedArray {
  const out = new Uint8ClampedArray(256)
  for (let i = 0; i < 256; i++) out[i] = i
  return out
}

/** グラデーションマップ: 輝度 0..255 → RGB */
export function gradientMapLut(stops: { pos: number; hex: string }[], reverse: boolean): Uint8ClampedArray {
  const out = new Uint8ClampedArray(256 * 4)
  const st = [...stops].map(s => ({ pos: reverse ? 1 - s.pos : s.pos, rgb: hexToRgb(s.hex) ?? { r: 0, g: 0, b: 0 } })).sort((a, b) => a.pos - b.pos)
  for (let i = 0; i < 256; i++) {
    const t = i / 255
    let r: number, g: number, b: number
    if (t <= st[0]!.pos) ({ r, g, b } = st[0]!.rgb)
    else if (t >= st[st.length - 1]!.pos) ({ r, g, b } = st[st.length - 1]!.rgb)
    else {
      let k = 0
      while (k + 1 < st.length && t > st[k + 1]!.pos) k++
      const a = st[k]!, c = st[k + 1]!
      const f = c.pos === a.pos ? 0 : (t - a.pos) / (c.pos - a.pos)
      r = a.rgb.r + (c.rgb.r - a.rgb.r) * f
      g = a.rgb.g + (c.rgb.g - a.rgb.g) * f
      b = a.rgb.b + (c.rgb.b - a.rgb.b) * f
    }
    out[i * 4] = r; out[i * 4 + 1] = g; out[i * 4 + 2] = b; out[i * 4 + 3] = 255
  }
  return out
}

/** LUT 系の調整に対して RGBA (256×4: R,G,B のそれぞれの LUT + 未使用) を返す。非 LUT 系は null */
export function buildLut(adj: Adjustment): Uint8ClampedArray | null {
  const pack = (r: Uint8ClampedArray, g: Uint8ClampedArray, b: Uint8ClampedArray) => {
    const out = new Uint8ClampedArray(256 * 4)
    for (let i = 0; i < 256; i++) { out[i * 4] = r[i]!; out[i * 4 + 1] = g[i]!; out[i * 4 + 2] = b[i]!; out[i * 4 + 3] = 255 }
    return out
  }
  const compose = (master: Uint8ClampedArray, ch?: Uint8ClampedArray) => {
    if (!ch) return master
    const out = new Uint8ClampedArray(256)
    for (let i = 0; i < 256; i++) out[i] = master[ch[i]!]!
    return out
  }
  switch (adj.kind) {
    case 'levels': {
      const m = levelsLut(adj.rgb)
      return pack(compose(m, adj.r && levelsLut(adj.r)), compose(m, adj.g && levelsLut(adj.g)), compose(m, adj.b && levelsLut(adj.b)))
    }
    case 'curves': {
      const m = curveLut(adj.rgb)
      return pack(compose(m, adj.r && curveLut(adj.r)), compose(m, adj.g && curveLut(adj.g)), compose(m, adj.b && curveLut(adj.b)))
    }
    case 'brightness-contrast': { const l = brightnessContrastLut(adj.brightness, adj.contrast); return pack(l, l, l) }
    case 'exposure': { const l = exposureLut(adj.exposure, adj.offset, adj.gamma); return pack(l, l, l) }
    case 'invert': { const l = invertLut(); return pack(l, l, l) }
    case 'posterize': { const l = posterizeLut(adj.levels); return pack(l, l, l) }
    case 'threshold': { const l = thresholdLut(adj.level); return pack(l, l, l) }
    case 'gradient-map': return gradientMapLut(adj.stops, adj.reverse)
    default:
      return null
  }
}

/** CPU 側の参照実装 (テストのオラクル): 1 画素に調整を適用する。非 LUT 系はシェーダと同じ式 */
/** 特定色域の選択: 各色域の重み (0..1)。Photoshop の定義に近い近似 */
export function selectiveColorWeights(r: number, g: number, b: number): Record<SelectiveColorRange, number> {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b)
  const pos = (v: number) => Math.max(0, v)
  return {
    reds: pos(r - Math.max(g, b)),
    yellows: pos(Math.min(r, g) - b),
    greens: pos(g - Math.max(r, b)),
    cyans: pos(Math.min(g, b) - r),
    blues: pos(b - Math.max(r, g)),
    magentas: pos(Math.min(r, b) - g),
    whites: pos((mn - 0.5) * 2),
    // 中間色系: 最大・最小がどちらも中間に近いほど強い (純色や白黒では 0)
    neutrals: Math.max(0, 1 - (Math.abs(2 * mx - 1) + Math.abs(2 * mn - 1)) / 2),
    blacks: pos((0.5 - mx) * 2)
  }
}
/** 特定色域の選択を 0..1 の RGB に適用 */
export function applySelectiveColor(adj: Extract<Adjustment, { kind: 'selective-color' }>, r: number, g: number, b: number): [number, number, number] {
  const w = selectiveColorWeights(r, g, b)
  let nr = r, ng = g, nb = b
  for (const name of SELECTIVE_COLOR_RANGES) {
    const k = w[name]
    if (k <= 0) continue
    const [c, m, y, kk] = adj.ranges[name]
    const cc = Math.max(-1, Math.min(1, (c + kk) / 100)), mm = Math.max(-1, Math.min(1, (m + kk) / 100)), yy = Math.max(-1, Math.min(1, (y + kk) / 100))
    const step = (v: number, s: number) => (adj.relative ? (s > 0 ? -s * v : -s * (1 - v)) : -s)
    nr += k * step(r, cc)
    ng += k * step(g, mm)
    nb += k * step(b, yy)
  }
  return [clamp01(nr), clamp01(ng), clamp01(nb)]
}

export function applyAdjustmentCpu(adj: Adjustment, r: number, g: number, b: number): [number, number, number] {
  const lut = buildLut(adj)
  if (lut) {
    if (adj.kind === 'gradient-map') {
      const lum = Math.round(0.299 * r + 0.587 * g + 0.114 * b)
      return [lut[lum * 4]!, lut[lum * 4 + 1]!, lut[lum * 4 + 2]!]
    }
    if (adj.kind === 'threshold') {
      const lum = 0.299 * r + 0.587 * g + 0.114 * b
      const v = lum >= adj.level ? 255 : 0
      return [v, v, v]
    }
    return [lut[r * 4]!, lut[g * 4 + 1]!, lut[b * 4 + 2]!]
  }
  const rf = r / 255, gf = g / 255, bf = b / 255
  const to8 = (v: number) => Math.round(clamp01(v) * 255)
  switch (adj.kind) {
    case 'hue-saturation': {
      const [h, s, l] = rgbToHsl(rf, gf, bf)
      if (adj.colorize) {
        const nl = clamp01(l + adj.colorizeLightness / 100 * (adj.colorizeLightness > 0 ? 1 - l : l))
        const [nr, ng, nb] = hslToRgb(adj.colorizeHue / 360, adj.colorizeSaturation / 100, nl)
        return [to8(nr), to8(ng), to8(nb)]
      }
      let nh = (h + adj.master.hue / 360) % 1
      if (nh < 0) nh += 1
      const sk = adj.master.saturation / 100
      const ns = clamp01(sk >= 0 ? s + (1 - s) * sk * (sk >= 1 ? 1 : sk) + s * sk * (1 - sk) : s * (1 + sk))
      const lk = adj.master.lightness / 100
      const nl = clamp01(lk >= 0 ? l + (1 - l) * lk : l * (1 + lk))
      const [nr, ng, nb] = hslToRgb(nh, ns, nl)
      return [to8(nr), to8(ng), to8(nb)]
    }
    case 'vibrance': {
      const mx = Math.max(rf, gf, bf), mn = Math.min(rf, gf, bf)
      const sat = mx - mn
      const avg = (rf + gf + bf) / 3
      const v = adj.vibrance / 100
      const sAmt = adj.saturation / 100
      // 自然な彩度: 低彩度ほど強く
      const k = 1 + v * (1 - sat) + sAmt
      return [to8(avg + (rf - avg) * k), to8(avg + (gf - avg) * k), to8(avg + (bf - avg) * k)]
    }
    case 'color-balance': {
      const lum = 0.299 * rf + 0.587 * gf + 0.114 * bf
      const ws = clamp01(1 - lum * 2), wh = clamp01(lum * 2 - 1), wm = 1 - ws - wh
      const ch = [rf, gf, bf].map((v, i) => v + (adj.shadows[i]! * ws + adj.midtones[i]! * wm + adj.highlights[i]! * wh) / 100 * 0.5)
      let [nr, ng, nb] = ch as [number, number, number]
      if (adj.preserveLuminosity) {
        const nl = 0.299 * nr + 0.587 * ng + 0.114 * nb
        const d = lum - nl
        nr += d; ng += d; nb += d
      }
      return [to8(nr), to8(ng), to8(nb)]
    }
    case 'black-white': {
      const [h, s] = rgbToHsl(rf, gf, bf)
      const lum = 0.299 * rf + 0.587 * gf + 0.114 * bf
      // 6 色相帯の重みを補間
      const bands = [adj.reds, adj.yellows, adj.greens, adj.cyans, adj.blues, adj.magentas]
      const hh = h * 6
      const i0 = Math.floor(hh) % 6, i1 = (i0 + 1) % 6
      const f = hh - Math.floor(hh)
      const w = (bands[i0]! * (1 - f) + bands[i1]! * f) / 100
      const gray = clamp01(lum + (w - 0.5) * s * 0.5)
      if (adj.tint) {
        const t = hexToRgb(adj.tintHex) ?? { r: 230, g: 200, b: 150 }
        return [to8(gray * (t.r / 255) * 1.2 + gray * 0.0), to8(gray * (t.g / 255) * 1.2), to8(gray * (t.b / 255) * 1.2)]
      }
      return [to8(gray), to8(gray), to8(gray)]
    }
    case 'photo-filter': {
      const c = hexToRgb(adj.hex) ?? { r: 236, g: 138, b: 0 }
      const d = adj.density / 100
      let nr = rf * (1 - d) + rf * (c.r / 255) * d
      let ng = gf * (1 - d) + gf * (c.g / 255) * d
      let nb = bf * (1 - d) + bf * (c.b / 255) * d
      if (adj.preserveLuminosity) {
        const lum = 0.299 * rf + 0.587 * gf + 0.114 * bf
        const nl = 0.299 * nr + 0.587 * ng + 0.114 * nb
        const k = nl > 0 ? lum / nl : 1
        nr *= k; ng *= k; nb *= k
      }
      return [to8(nr), to8(ng), to8(nb)]
    }
    case 'channel-mixer': {
      const mix = (row: [number, number, number, number]) => (row[0] * rf + row[1] * gf + row[2] * bf + row[3]) / 100
      if (adj.monochrome) { const v = mix(adj.r); return [to8(v), to8(v), to8(v)] }
      return [to8(mix(adj.r)), to8(mix(adj.g)), to8(mix(adj.b))]
    }
    case 'selective-color': {
      const [nr, ng, nb] = applySelectiveColor(adj, rf, gf, bf)
      return [to8(nr), to8(ng), to8(nb)]
    }
    case 'color-lookup': {
      const s = adj.lutPixelId ? pixelStore.get(adj.lutPixelId) : undefined
      if (!s || adj.size < 2) return [r, g, b]
      const N = adj.size
      const tiled = s.readRect({ x: 0, y: 0, w: N * N, h: N })
      const [nr, ng, nb] = sampleTiledLut(tiled, N, rf, gf, bf)
      return [to8(nr), to8(ng), to8(nb)]
    }
    default:
      return [r, g, b]
  }
}

export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b)
  const l = (mx + mn) / 2
  if (mx === mn) return [0, 0, l]
  const d = mx - mn
  const s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn)
  let h: number
  if (mx === r) h = (g - b) / d + (g < b ? 6 : 0)
  else if (mx === g) h = (b - r) / d + 2
  else h = (r - g) / d + 4
  return [h / 6, s, l]
}

export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l, l, l]
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const f = (t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  return [f(h + 1 / 3), f(h), f(h - 1 / 3)]
}
